import {
  CompositeDef,
  CompositeExpr,
  CompositeNode,
  CompositeNodeRef,
  CompositeOutput,
  CompositeRef,
  CompositeSourceRef,
} from '../../../types/composite'
import { computeMinCandles } from './computeWarmup'
import { isNodeRef } from './refs'

const remapNodeRef = (ref: CompositeNodeRef, ids: Map<string, string>): CompositeNodeRef => {
  const mapped: CompositeNodeRef = { node: ids.get(ref.node) ?? ref.node }
  if (ref.output) mapped.output = ref.output
  if (ref.offset) mapped.offset = ref.offset // strips 0 and undefined
  return mapped
}

const remapRef = (ref: CompositeRef, ids: Map<string, string>): CompositeRef => {
  if (!isNodeRef(ref)) {
    const mapped: CompositeSourceRef = { source: ref.source }
    if (ref.offset) mapped.offset = ref.offset
    return mapped
  }
  return remapNodeRef(ref, ids)
}

const remapExpr = (expr: CompositeExpr, ids: Map<string, string>): CompositeExpr => {
  if (expr.t === 'const') return { t: 'const', value: expr.value }
  if (expr.t === 'ref') return { t: 'ref', ref: remapRef(expr.ref, ids) }
  return { t: 'op', op: expr.op, args: expr.args.map((a) => remapExpr(a, ids)) }
}

/**
 * Produces the canonical, hash-stable form of a definition: step ids renumbered
 * n1..nk in array order, every ref remapped, empty optionals stripped, and
 * minCandles computed. The canonical def is what gets embedded in patterns and
 * hashed by getIndicatorHash, so this mapping must stay deterministic.
 */
export const canonicalizeDef = (input: {
  id: string
  version: number
  name: string
  nodes: CompositeNode[]
  outputs: CompositeOutput[]
  scale: 'overlay' | 'oscillator'
}): CompositeDef => {
  const ids = new Map(input.nodes.map((n, i) => [n.id, `n${i + 1}`]))
  const nodes: CompositeNode[] = input.nodes.map((node) => {
    const id = ids.get(node.id)!
    if (node.kind === 'indicator') {
      const mapped: CompositeNode = {
        id,
        kind: 'indicator',
        name: node.name,
        params: { ...node.params },
      }
      if (node.input) mapped.input = remapRef(node.input, ids)
      return mapped
    }
    return { id, kind: 'math', expr: remapExpr(node.expr, ids) }
  })
  const outputs = input.outputs.map((o) => ({ field: o.field, ref: remapNodeRef(o.ref, ids) }))
  return {
    v: 1,
    id: input.id,
    version: input.version,
    name: input.name,
    nodes,
    outputs,
    minCandles: computeMinCandles(nodes, outputs),
    scale: input.scale,
  }
}
